import escapeRegExp from 'lodash.escaperegexp';
import flatten from 'lodash.flatten';
import { getQuery, getDynamicQuery } from '../../application';
import { KEYWORD } from '../../constants';
import getAdminRecordSearch from './get-admin-record-search';
import createEntityRecord from './create-entity-record';
import updateEntityRecord from './update-entity-record';

const fetchBannerSizes = async ({ client }) => {
  const {
    data: { banners: bannerSizes }
  } = await client.query({
    query: getQuery('GET_BANNERS')
  });
  return bannerSizes;
};

const fetchEntities = async ({ client }) => {
  const {
    data: { getEntitySchemas }
  } = await client.query({
    query: getQuery('GET_USER_EDITABLE_ENTITIES')
  });
  return getEntitySchemas;
};

const fetchAllEntities = async ({ client }) => {
  const {
    data: { getEntitySchemas }
  } = await client.query({
    query: getQuery('GET_ALL_ENTITY_SCHEMA')
  });

  return getEntitySchemas;
};

const fetchPublishedContent = async ({ client }) => {
  const {
    data: { getEntitySchemas }
  } = await client.query({
    query: getQuery('GET_PUBLISHED_CONTENT')
  });

  return getEntitySchemas;
};

const fetchEntitiesProperties = async ({ client, entity }) => {
  const {
    data: { getEntitySchemas }
  } = await client
    .query({
      query: getQuery('GET_ENTITY_SCHEMA'),
      variables: {
        identifier: entity
      }
    })
    .catch(e => console.error(e)); // eslint-disable-line
  if (!getEntitySchemas.length) return;

  return getEntitySchemas;
};

const fetchSearchResults = async (query, entities, client) => {
  if (typeof entities === 'string') return fetchSearchResultsByEntity(query, entities, client);
  const selectedEntities = entities.map(opt => opt.split('/')[1]);
  const results = selectedEntities.map(entity => fetchSearchResultsByEntity(query, entity, client));
  const resolvedResults = await Promise.all(results);
  return resolvedResults.map((result, i) => {
    if (result.length) {
      return result.map(res => ({
        ...res,
        id: `${entities[i].split('/')[0]}/${res.id}`
      }));
    }
    return result;
  });
};

const fetchSearchResultsByEntity = async (query, entity, client) => {
  const [schema] = await fetchEntitiesProperties({ client, entity });
  const searchQuery = getDynamicQuery('RELATION_SEARCH_QUERY')(schema);
  const {
    data: { searchData }
  } = await client
    .query({
      query: searchQuery,
      variables: {
        where: {
          name: { _ilike: `%${query}%` }
        }
      }
    })
    .catch(e => console.error(e)); // eslint-disable-line
  return searchData;
};

const fetchSelectedRelations = async (client, ids, entityIdentifiers) => {
  const results = entityIdentifiers.map(entity => {
    const entityIdentifier = entity.split('/')[1];
    return fetchSelectedRelation(client, ids, entityIdentifier);
  });
  const resolvedResults = await Promise.all(results);

  return flatten(
    resolvedResults.map(({ result }, i) => {
      const [currentEntity] = entityIdentifiers[i].split('/');
      return result.map(({ id, name }) => ({ id: `${currentEntity}/${id}`, name }));
    })
  );
};

const fetchSelectedRelation = async (client, ids, entityIdentifier) => {
  const schema = await fetchRelationSchema(client, entityIdentifier);
  const parsedIds = ids.map(id => id.split('/')[1]);
  const { data: selected } = await client
    .query({
      query: getDynamicQuery('GET_ALL_ENTITIES')(schema),
      variables: {
        where: {
          id: {
            _in: parsedIds
          }
        }
      }
    })
    // eslint-disable-next-line no-console
    .catch(e => console.error(e));
  return selected;
};

const fetchSelectedEntities = async (client, ids, entity) => {
  const [schema] = await fetchEntitiesProperties({ client, entity });
  const { data: selected } = await client
    .query({
      query: getDynamicQuery('GET_ALL_ENTITIES')(schema),
      variables: {
        where: {
          id: {
            _in: ids
          }
        }
      }
    })
    // eslint-disable-next-line no-console
    .catch(e => console.error(e));
  return selected;
};

const fetchRelationSchema = async (client, entityIdentifier) => {
  if (!entityIdentifier) return null;

  const {
    data: {
      getEntitySchemas: [relationSchema]
    }
  } = await client.query({
    query: getQuery('GET_ENTITY_SCHEMA'),
    variables: { identifier: entityIdentifier }
  });
  return relationSchema;
};

const fetchCurrentClientEntity = async client => {
  const { data = {} } = await client.query({
    query: getQuery('GET_CURRENT_ENTITY')
  });
  return (data && data.currentEntity) || '';
};

const buildShouldValues = entities =>
  entities.map(docType => ({
    match: {
      docType
    }
  }));

const fetchFilterByDataFromElastic = async (
  client,
  currentValues,
  relations,
  stringTypeProps,
  searchValue
) => {
  const aggs = {};
  const { entity, entities, filterByProperty = [] } = currentValues;
  const entitiesArray = entities || [entity];
  filterByProperty.forEach(fieldName => {
    const [propName] = fieldName.split('/');
    let shouldAppendKeyword = stringTypeProps.includes(propName);
    if (propName.includes('.')) {
      const [relationName, relationProp] = propName.split('.');
      const relation = relations.find(({ localField }) => localField === relationName);
      const { entityIdentifier = '' } = relation || {};
      if (entityIdentifier && entityIdentifier !== relationName) {
        shouldAppendKeyword = stringTypeProps.includes(`${entityIdentifier}.${relationProp}`);
      }
    }
    aggs[propName] = {
      terms: {
        field: `${propName}${shouldAppendKeyword ? KEYWORD : ''}`,
        include: `${escapeRegExp(searchValue)}.*`,
        size: 2147483647 // large number to get all
      }
    };
  });

  const rawQueryStringified = JSON.stringify({
    aggs,
    size: 0,
    query: {
      bool: {
        filter: {
          bool: {
            should: buildShouldValues(entitiesArray),
            minimum_should_match: 1
          }
        }
      }
    }
  });

  const { data = {} } = await client.query({
    query: getQuery('SEARCH_ADMIN'),
    variables: { rawQueryStringified }
  });
  const aggregatedData =
    data.search && data.search.rawResults && data.search.rawResults.aggregations;

  return aggregatedData || {};
};

const fetchTemplate = async (client, templateId) => {
  // The active template is the published version (or a legacy row saved
  // before publishing existed) — record editing works against what audiences
  // see, never against in-flight draft template edits.
  const {
    data: {
      getActivePageBuilderTemplate: {
        componentsAllowedChildChanges = [],
        pageBuilder: pageBuilderComponents = []
      } = {}
    } = {}
  } = await client.query({
    query: getQuery('GET_PB_TEMPLATE'),
    variables: {
      id: templateId
    },
    fetchPolicy: 'network-only'
  });

  return { pageBuilderComponents, componentsAllowedChildChanges };
};

export {
  createEntityRecord,
  fetchBannerSizes,
  fetchEntities,
  fetchEntitiesProperties,
  fetchRelationSchema,
  fetchSearchResults,
  fetchSelectedRelations,
  fetchAllEntities,
  fetchSelectedEntities,
  fetchCurrentClientEntity,
  fetchFilterByDataFromElastic,
  fetchTemplate,
  fetchPublishedContent,
  getAdminRecordSearch,
  updateEntityRecord
};
